'use client'

import Script from 'next/script'
import { useState } from 'react'
import { useDeferredTag } from '@/hooks/useDeferredTag'
import { META_PIXEL_ID } from '@/lib/tracking/config'
import { readClientConsent } from '@/lib/tracking/consent'
import type { DeferredTagSpec } from '@/lib/tracking/deferred-tags'

/**
 * Meta base pixel — first-party <Script>, consent-gated, no GTM (deliberately
 * removed site-wide). Never Partytown/worker — fbevents.js CORS-preflight
 * breaks under it (unfixed upstream).
 *
 * Two-stage load (fbevents.js was the single largest main-thread cost on the
 * exam PDP, ~540 ms of a 500–930 ms TBT):
 *  - Stage 1, afterInteractive, unchanged instant: Meta's official stub minus
 *    its own <script> insertion. It defines the `fbq` queue, queues
 *    `fbq('init')` and the landing `fbq('track','PageView')` from the first
 *    hydration effect, inside the ConsentGate subtree — ahead of every
 *    page-level effect — so ViewContent / AddToCart / InitiateCheckout /
 *    Purchase queue behind them in order. Nothing the page fires is lost.
 *  - Stage 2: fbevents.js itself is injected by `useDeferredTag` on the first
 *    user interaction, or FBEVENTS_FALLBACK_MS after `load`, whichever comes
 *    first. On execution it drains the queue and writes `_fbp`.
 *
 * The fast-buyer race is closed at the checkout boundary, not here: `useCheckout`
 * awaits `whenDeferredTagReady('fbevents')` (bounded) before POSTing to
 * create-checkout, which copies `_fbp`/`_fbc` from the request cookies into
 * Stripe metadata for the webhook CAPI Purchase. `_fbc` also has the server
 * fallback built from the proxy's `_ps_fbclid`. gtag is NOT deferred — GA4
 * session stitching (`_ga`/`_ga_<id>`) has no such fallback.
 *
 * Consent (master plan §5.2): denied — opt-out, GPC, or gated geo — renders
 * NOTHING. No fbevents.js, no fbq stub, no listeners, so every `window.fbq?.()`
 * call site no-ops structurally. Consent changes take effect via full page
 * reload (the privacy panel reloads after writing the cookie).
 *
 * Known-visitor match help (C-H1, replaces dataset-level AAM which stays OFF):
 * a returning lead/buyer's stored email is passed to fbq('init') as manual
 * advanced matching — fbevents.js SHA-256-hashes it client-side before any
 * network call. Only runs when consent is granted, which gates this component
 * entirely.
 */

/** No-interaction ceiling for fbevents.js, measured from `load`. Lighthouse
 *  keeps tracing until the page is network- and CPU-quiet after load, which on
 *  this page ran past load + 4 s in one of three local runs (fbevents then
 *  executed inside the trace: 367 ms blocking); load + 6 s cleared it. Anyone
 *  who stays on the page still produces the PageView; `_fbp` at checkout does
 *  not depend on this timer (see useCheckout). */
const FBEVENTS_FALLBACK_MS = 6000

const FBEVENTS: DeferredTagSpec = {
  id: 'fbevents',
  src: 'https://connect.facebook.net/en_US/fbevents.js',
  fallbackMs: FBEVENTS_FALLBACK_MS,
}

export function MetaPixel() {
  // Read once at mount: consent flips only via the privacy panel, which
  // reloads the page. useState initializer keeps this out of render churn.
  const [allowed] = useState<boolean>(
    () => Boolean(META_PIXEL_ID) && readClientConsent() === 'granted'
  )
  useDeferredTag(FBEVENTS, allowed)

  if (!allowed) return null

  // Queue semantics (callMethod / queue / push / loaded / version / _fbq) are
  // verbatim from Meta's snippet so fbevents.js drains the queue exactly as if
  // it had inserted the tag itself.
  return (
    <Script id="meta-pixel" strategy="afterInteractive">
      {`!function(f,n){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[]}(window);
(function(){var em;try{em=localStorage.getItem('brevo_email')||undefined}catch(e){}
if(em){window.fbq('init','${META_PIXEL_ID}',{em:em})}else{window.fbq('init','${META_PIXEL_ID}')}
window.fbq('track','PageView');})();`}
    </Script>
  )
}
